import { createApi } from '@reduxjs/toolkit/query/react';
import { ERPUserDTOType } from '../../schemas/identity';
import { overrideFetchBaseQuery } from '../utils/overrideFetchBaseQuery';
import { baseQueryWithZodValidation } from '../utils/baseQueryWithZodValidation';
import type {
  BaseQueryApi,
  FetchArgs,
  QueryReturnValue,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import type { IAuthState } from './types';
import { clearAuth } from './slice';

export interface GoogleAuthRequest {
  credential: string;
}

export interface AuthResponse {
  user: {
    id: number;
    email: string;
  };
  token: string;
  refreshToken: string;
  erpUser: ERPUserDTOType;
}

type SynqAuthState = { identity: IAuthState };

export const identityApi = createApi({
  reducerPath: 'identityApi',
  baseQuery: baseQueryWithZodValidation(overrideFetchBaseQuery({}), {}),
  tagTypes: ['Auth'],
  endpoints: builder => ({
    verifyGoogleAuth: builder.mutation<
      AuthResponse,
      {
        id: string;
        authMode: 'GOOGLE';
      }
    >({
      query: credentials => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),
    logout: builder.mutation<void, void>({
      async queryFn(
        _arg: void,
        { getState, dispatch }: BaseQueryApi,
        _extraOptions,
        baseQuery: (
          arg: string | FetchArgs,
        ) => Promise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>>,
      ): Promise<QueryReturnValue<void, FetchBaseQueryError, FetchBaseQueryMeta>> {
        const { token, refreshToken } = (getState() as SynqAuthState).identity;

        try {
          const result = await baseQuery({
            url: '/auth/logout',
            method: 'POST',
            body: { token, refreshToken },
          });
          if (result.error) {
            return { error: result.error };
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          dispatch(clearAuth());
        }

        return { data: undefined };
      },
      invalidatesTags: ['Auth'],
    }),
  }),
});

export const { useVerifyGoogleAuthMutation, useLogoutMutation } = identityApi;
